import { createSelector } from 'reselect'
import { ConfigManager } from '~/managers/ConfigManager'
import { CommonState } from '~/store/types'
import { BUSINESS_VERIFICATION_STATUS, PERSONAL_VERIFICATION_STATUS } from '~/types'

const selectUserInfoState = (state: CommonState) => state.userInfo || state.merchantInfo

const selectUserInfo = createSelector([selectUserInfoState], (state) => state.userInfo)

export const selectMerchantDetail = createSelector(
  [selectUserInfoState],
  (state) => state.merchantDetail
)

const selectUserProfile = createSelector([selectUserInfoState], (state) => state.userProfile)

export const selectUserRequiresManualPeriodicReview = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.periodicReviewRequired && !userInfo.automaticPeriodicReviewEnabled
)

export const selectUserRequiresAutoPeriodicReview = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.periodicReviewRequired && userInfo.automaticPeriodicReviewEnabled
)

export const selectVerificationStatus = createSelector(
  [selectUserInfo, selectMerchantDetail],
  (userInfo, merchantDetail) => {
    if (ConfigManager.isAppSite()) {
      return userInfo.verificationStatus
    }

    return merchantDetail?.verificationStatus || BUSINESS_VERIFICATION_STATUS.INITIAL
  }
)

export const selectEmail = createSelector([selectUserInfo], (userInfo) => userInfo.email)

export const selectIsKycVerified = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.kycVerified
)

export const selectIsriskModalTncAccepted = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.riskModalTncAccepted
)

export const selectIsUserFullyVerified = createSelector([selectUserInfo], (userInfo) => {
  return userInfo.verificationStatus === PERSONAL_VERIFICATION_STATUS.VERIFIED && !!userInfo.phoneNo
})

export const selectKycVerifiedDate = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.kycVerifiedDate
)

export const selectMerchantId = createSelector(
  [selectUserProfile],
  (userProfile) => userProfile?.merchantId
)

export const selectIsUserScbRailsEnabled = createSelector([selectUserInfo], (userInfo) => {
  if (ConfigManager.isAppSite()) {
    return false
  }

  return userInfo?.profileConfigurations?.usdScbRailsEnabled || false
})

export const selectIs2FAEnabled = createSelector([selectUserInfo], (userInfo) => {
  return userInfo.gauthEnabled
})

export const selectUserName = createSelector(
  [selectUserInfo, selectMerchantDetail],
  (userInfo, merchantDetail) => {
    if (ConfigManager.isAppSite()) {
      return userInfo.fullName || userInfo.firstName
    }

    return merchantDetail?.businessName
  }
)

export const selectCkaPassed = createSelector([selectUserInfo], (userInfo) => userInfo.ckaPassed)

export const selectIsAccountLocked = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.accountLocked
)

export const selectUserActivityStatus = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.activityStatus
)

export const selectIsUserChineseResident = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.country?.toUpperCase() === 'CHINA'
)

export const selectIsUserFiatToFiatBusiness = createSelector([selectUserInfo], (userInfo) => {
  return userInfo?.profileConfigurations?.fiatToFiatBusiness || false
})

export const selectPeriodicReviewOverdueDate = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo.periodicReviewOverdueDate
)
